import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DoCheck, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { SharedService } from '../../../_services/shared.service';
import { ThemeService } from '../../../_services/theme.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ResponsiveMode } from '../../../enum/responsiveModes';
import { Subscription } from 'rxjs';

type PropertyLogoModeConfig = {
  prop_logo_url?: string | null;
  prop_logo_aspect_ratio?: string | null;
};

type PropertyLogosConfig = {
  light_mode?: PropertyLogoModeConfig | null;
  dark_mode?: PropertyLogoModeConfig | null;
};

@Component({
  selector: 'ui-new-top-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ui-new-top-bar.component.html',
  styleUrls: ['./ui-new-top-bar.component.scss'],
})

export class UiNewTopBarComponent implements OnInit, DoCheck {
  @Input() logoUrl: string | null = null;

  @Output() refreshClicked = new EventEmitter<void>();
  @Output() logoRendered = new EventEmitter<void>();

  @ViewChild('desktopLogoImage') private desktopLogoImage?: ElementRef<HTMLImageElement>;
  @ViewChild('handsetLogoImage') private handsetLogoImage?: ElementRef<HTMLImageElement>;

  // Component state
  viewMode: string = ResponsiveMode.horizontal;
  responsiveMode = ResponsiveMode;
  hasLogo = false;
  logoAspectRatio = '1 / 1';
  private lastResolvedLogoUrl: string | null = null;
  private lastResolvedAspectRatio = '1 / 1';
  private hasEmittedLogoRendered = false;

  private breakpointSubscription?: Subscription;

  constructor(
    public sharedService: SharedService,
    public themeService: ThemeService,
    private responsive: BreakpointObserver,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.syncLogoConfig();

    this.breakpointSubscription = this.responsive.observe([Breakpoints.HandsetPortrait])
      .subscribe(result => {
        const isHandset = result.breakpoints[Breakpoints.HandsetPortrait];
        this.viewMode = isHandset ? ResponsiveMode.vertical : ResponsiveMode.horizontal;
        this.cdr.detectChanges();
      });
  }

  ngDoCheck(): void {
    this.syncLogoConfig();
  }

  private syncLogoConfig(): void {
    const prestayConfig = this.sharedService.applicationConfig?.prestay ?? null;
    const propertyLogos = (prestayConfig as { prop_logos?: PropertyLogosConfig | null; PropertyLogos?: PropertyLogosConfig | null } | null)?.prop_logos
      ?? (prestayConfig as { prop_logos?: PropertyLogosConfig | null; PropertyLogos?: PropertyLogosConfig | null } | null)?.PropertyLogos
      ?? null;
    const activeModeLogo = this.getLogoConfigForCurrentMode(propertyLogos);

    const configuredUrl = activeModeLogo?.prop_logo_url?.trim() ?? '';
    const inputUrl = this.logoUrl?.trim() ?? '';
    const resolvedUrl = configuredUrl || inputUrl;

    const nextLogoUrl = this.isValidImageUrl(resolvedUrl) ? resolvedUrl : null;
    const nextAspectRatio = this.parseAspectRatio(activeModeLogo?.prop_logo_aspect_ratio);

    if (nextLogoUrl === this.lastResolvedLogoUrl && nextAspectRatio === this.lastResolvedAspectRatio) {
      return;
    }

    this.logoUrl = nextLogoUrl;
    this.hasLogo = !!this.logoUrl;
    this.logoAspectRatio = nextAspectRatio;
    this.lastResolvedLogoUrl = nextLogoUrl;
    this.lastResolvedAspectRatio = nextAspectRatio;

    if (!this.hasLogo) {
      this.emitLogoRenderedOnce();
      return;
    }

    this.hasEmittedLogoRendered = false;
  }

  onLogoError(): void {
    this.logoUrl = null;
    this.hasLogo = false;
    this.lastResolvedLogoUrl = null;
    this.emitLogoRenderedOnce();
    this.cdr.detectChanges();
  }

  onLogoLoad(): void {
    this.emitLogoRenderedOnce();
  }

  private getLogoConfigForCurrentMode(propertyLogos: PropertyLogosConfig | null): PropertyLogoModeConfig | null {
    if (!propertyLogos) {
      return null;
    }

    return this.themeService.isDarkMode()
      ? propertyLogos.dark_mode ?? null
      : propertyLogos.light_mode ?? null;
  }

  private isValidImageUrl(url: string | null | undefined): boolean {
    if (!url) return false;

    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private parseAspectRatio(value: string | null | undefined): string {
    const raw = (value ?? '').trim();
    const match = raw.match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/i);

    if (!match) return '1 / 1';

    const width = Number(match[1]);
    const height = Number(match[2]);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return '1 / 1';
    }

    return `${width} / ${height}`;
  }

  refresh() {
    this.refreshClicked.emit();
  }

  onThemeModeSelected(isDark: boolean): void {
    // Top-bar clicks represent explicit user intent and should pin mode.
    this.themeService.setDarkModeFromUser(isDark);
  }

  private emitLogoRenderedOnce(): void {
    if (this.hasEmittedLogoRendered) {
      return;
    }

    this.hasEmittedLogoRendered = true;
    queueMicrotask(() => this.logoRendered.emit());
  }

  ngOnDestroy(): void {
    this.breakpointSubscription?.unsubscribe();
  }
}
